import { useState, useEffect } from 'react';
import { checkIsAdmin, checkIsCustomer, UserInformation } from '../authorization/UserInformation';
import { msalInit } from '../authorization/AuthorizationProvider';
import { getToken } from '../authorization/AuthorizationUtils';

interface UserHookReturn {
    userInformation: UserInformation | null;
    loading: boolean;
    isAdmin: boolean;
    isClient: boolean;
    error: string | null;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const useUser = (): UserHookReturn => {
    const [loading, setLoading] = useState(true);
    const [userInformation, setUserInformation] = useState<UserInformation | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let retryCount = 0;
        let mounted = true;

        const initializeUserInformation = async () => {
            try {
                // Clear previous errors
                setError(null);

                // Ensure MSAL is initialized
                await msalInit;

                // Verify token exists
                const token = await getToken();
                if (!token) {
                    throw new Error('No valid authentication token found');
                }

                // Get user information
                const userInfo = await UserInformation.fromTokenClaims();
                if (!userInfo && mounted) {
                    throw new Error('Failed to get user information');
                }

                // Get role information
                const [adminCheck, clientCheck] = await Promise.all([
                    checkIsAdmin(),
                    checkIsCustomer()
                ]);

                if (mounted) {
                    setUserInformation(userInfo);
                    setIsAdmin(adminCheck);
                    setIsClient(clientCheck);
                    
                    // Cache role information
                    localStorage.setItem('userRoles', JSON.stringify({ 
                        isAdmin: adminCheck, 
                        isClient: clientCheck 
                    }));
                }
            } catch (error) {
                if (!mounted) return;

                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                console.error('Error in user initialization:', errorMessage);

                if (retryCount < MAX_RETRIES) {
                    retryCount++;
                    console.log(`Retrying initialization (${retryCount}/${MAX_RETRIES})...`);
                    setTimeout(initializeUserInformation, RETRY_DELAY);
                    return;
                }

                setError(errorMessage);
                setUserInformation(null);
                setIsAdmin(false);
                setIsClient(false);
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        initializeUserInformation();

        return () => {
            mounted = false;
        };
    }, []);

    return { userInformation, loading, isAdmin, isClient, error };
};

export default useUser;
